import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Expense, ExpenseCategoriesApi, ExpensesApi } from '@/services/expenses.api';
import { AcademicYearsApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const expenseSchema = z.object({
  title: z.string().min(1, 'Required'),
  description: z.string().optional(),
  amount: z.coerce.number().min(0.01, 'Must be greater than 0'),
  date: z.string().min(1, 'Required'),
  categoryId: z.string().optional(),
});
type FormValues = z.infer<typeof expenseSchema>;

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

function formatRupees(n: number): string {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function ExpensesPage() {
  const qc = useQueryClient();
  const [categoryFilter, setCategoryFilter] = useState('');
  const [chartYearId, setChartYearId] = useState('');
  const [modal, setModal] = useState<{ open: boolean; expense?: Expense }>({ open: false });
  const [confirm, setConfirm] = useState<{ open: boolean; expense?: Expense }>({ open: false });

  const { data: categories = [] } = useQuery({
    queryKey: ['expense-categories', 'active'],
    queryFn: () => ExpenseCategoriesApi.list(false),
  });

  const { data: currentYear } = useQuery({ queryKey: ['academic-years', 'current'], queryFn: () => AcademicYearsApi.current() });
  const { data: yearsPage } = useQuery({
    queryKey: ['academic-years', 'all-for-expenses'],
    queryFn: () => AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
  });
  const years = yearsPage?.items ?? [];
  // Defaults to the current academic year, but a school's real expense
  // history often lives in a prior year — expose a picker rather than
  // silently showing an all-zero chart with no way to see the real data.
  const effectiveChartYearId = chartYearId || currentYear?.id || '';

  const { data: expenses = [], isLoading } = useQuery({
    queryKey: ['expenses', categoryFilter],
    queryFn: () => ExpensesApi.list({ categoryId: categoryFilter || undefined }),
  });

  const { data: monthTotals = [] } = useQuery({
    queryKey: ['expenses', 'filter-graph', effectiveChartYearId],
    queryFn: () => ExpensesApi.filterGraph(effectiveChartYearId),
    enabled: !!effectiveChartYearId,
  });

  const chartData = useMemo(() => monthTotals.map((m) => ({ month: MONTH_NAMES[m.month - 1], total: m.total })), [monthTotals]);
  const categoryNameById = useMemo(() => new Map(categories.map((c) => [c.id, c.name])), [categories]);
  const chartYearName = years.find((y) => y.id === effectiveChartYearId)?.name ?? currentYear?.name;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['expenses'] });

  const upsert = useMutation({
    mutationFn: (vals: { id?: string; payload: FormValues }) =>
      vals.id ? ExpensesApi.update(vals.id, vals.payload) : ExpensesApi.create(vals.payload),
    onSuccess: () => {
      invalidate();
      setModal({ open: false });
    },
  });

  const removeMut = useMutation({
    mutationFn: (id: string) => ExpensesApi.remove(id),
    onSuccess: () => {
      invalidate();
      setConfirm({ open: false });
    },
  });

  return (
    <>
      <PageHeader
        title="Manage Expenses"
        description="Record and track school expenses by category."
        actions={
          <button className="btn-primary" onClick={() => setModal({ open: true })}>
            <Plus className="mr-1.5 h-4 w-4" /> New expense
          </button>
        }
      />

      {effectiveChartYearId && (
        <div className="mb-4 h-64 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Monthly expenses — {chartYearName}</span>
            <Select className="!w-40" value={effectiveChartYearId} onChange={(e) => setChartYearId(e.target.value)}>
              {years.map((y) => (
                <option key={y.id} value={y.id}>
                  {y.name}
                </option>
              ))}
            </Select>
          </div>
          <ResponsiveContainer width="100%" height="80%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v: number) => [formatRupees(v), 'Total']} />
              <Bar dataKey="total" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-52" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </div>

      <DataTable<Expense>
        rows={expenses}
        getRowId={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="No expenses yet. Create your first one."
        columns={[
          { key: 'title', header: 'Title', render: (e) => <span className="font-medium text-slate-900">{e.title}</span> },
          { key: 'category', header: 'Category', render: (e) => (e.categoryId ? (categoryNameById.get(e.categoryId) ?? '—') : '—') },
          { key: 'date', header: 'Date', render: (e) => e.date },
          { key: 'amount', header: 'Amount', render: (e) => <span className="font-medium">{formatRupees(e.amount)}</span> },
        ]}
        actions={(e) => (
          <>
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
              onClick={() => setModal({ open: true, expense: e })}
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
              onClick={() => setConfirm({ open: true, expense: e })}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        )}
      />

      <ExpenseFormModal
        open={modal.open}
        expense={modal.expense}
        categories={categories}
        onClose={() => setModal({ open: false })}
        onSubmit={(vals) => upsert.mutate({ id: modal.expense?.id, payload: { ...vals, categoryId: vals.categoryId || undefined } })}
        saving={upsert.isPending}
        errorMsg={errMsg(upsert.error)}
      />

      <ConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        onConfirm={() => confirm.expense && removeMut.mutate(confirm.expense.id)}
        loading={removeMut.isPending}
        title="Delete expense?"
        message={`This will permanently delete "${confirm.expense?.title}".`}
        confirmText="Delete expense"
      />
    </>
  );
}

function ExpenseFormModal({
  open,
  expense,
  categories,
  onClose,
  onSubmit,
  saving,
  errorMsg,
}: {
  open: boolean;
  expense?: Expense;
  categories: { id: string; name: string }[];
  onClose: () => void;
  onSubmit: (v: FormValues) => void;
  saving: boolean;
  errorMsg?: string;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(expenseSchema),
    values: {
      title: expense?.title ?? '',
      description: expense?.description ?? '',
      amount: expense?.amount ?? 0,
      date: expense?.date ?? new Date().toISOString().slice(0, 10),
      categoryId: expense?.categoryId ?? '',
    },
  });

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={expense ? `Edit ${expense.title}` : 'New expense'}
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleSubmit(onSubmit)} disabled={saving}>
            {saving ? 'Saving…' : expense ? 'Save changes' : 'Create expense'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Title" required className="sm:col-span-2" error={errors.title?.message}>
          <Input {...register('title')} placeholder="Electricity bill — March" />
        </Field>
        <Field label="Amount (₹)" required error={errors.amount?.message}>
          <Input type="number" min={0} step="0.01" {...register('amount')} />
        </Field>
        <Field label="Date" required error={errors.date?.message}>
          <Input type="date" {...register('date')} />
        </Field>
        <Field label="Category" className="sm:col-span-2" error={errors.categoryId?.message}>
          <Select {...register('categoryId')}>
            <option value="">No category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Description" className="sm:col-span-2">
          <Textarea {...register('description')} rows={2} />
        </Field>
        {errorMsg && <div className="sm:col-span-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMsg}</div>}
      </form>
    </Modal>
  );
}
